import { useCallback, useRef, useState } from "react";

import {
  addTrack,
  draftFromFileName,
  type UploadStage,
} from "../../api/library.js";
import type { Track } from "../../api/types.js";

/**
 * Adding music, one file or fifty.
 *
 * Dropping a folder of music is the ordinary case, so the queue is the design rather
 * than a dialog per file: every file is taken as it is, named from its own file name,
 * and reports its own outcome. Nothing is asked of the operator up front, because the
 * metadata is editable the moment the track exists and asking fifty times before the
 * first byte moves would be the wrong trade.
 *
 * Two at a time. The API validates by downloading and decoding what was staged, so a
 * wide fan-out costs the API more than it saves the operator.
 */

const CONCURRENCY = 2;

export type QueueState =
  "waiting" | UploadStage | "added" | "already-in-library" | "failed";

export interface QueueEntry {
  key: string;
  fileName: string;
  title: string;
  artist: string;
  byteSize: number;
  state: QueueState;
  /** What the API said, when it refused. Its own words, not a paraphrase. */
  detail: string;
  trackId: string | null;
}

export const QUEUE_STATE_LABELS: Record<QueueState, string> = {
  waiting: "Waiting",
  hashing: "Checksumming",
  uploading: "Uploading",
  validating: "Validating",
  added: "Added",
  "already-in-library": "Already in the library",
  failed: "Not added",
};

export function isSettled(state: QueueState): boolean {
  return (
    state === "added" || state === "already-in-library" || state === "failed"
  );
}

export interface UploadQueue {
  entries: QueueEntry[];
  running: boolean;
  add: (files: readonly File[]) => void;
  clearSettled: () => void;
}

/**
 * `onAdded` is handed every track the API returned, including the ones it answered
 * with 200 because the library already held that audio. The library deduplicates by
 * content, globally, so "already there" is a normal outcome and not a failure.
 */
export function useUploadQueue(onAdded: (track: Track) => void): UploadQueue {
  const [entries, setEntries] = useState<QueueEntry[]>([]);
  const [running, setRunning] = useState(false);
  const pending = useRef<{ key: string; file: File }[]>([]);
  const active = useRef(0);
  const sequence = useRef(0);
  const added = useRef(onAdded);
  added.current = onAdded;

  const update = useCallback((key: string, patch: Partial<QueueEntry>) => {
    setEntries((current) =>
      current.map((entry) =>
        entry.key === key ? { ...entry, ...patch } : entry,
      ),
    );
  }, []);

  const pump = useCallback(() => {
    while (active.current < CONCURRENCY && pending.current.length > 0) {
      const next = pending.current.shift();
      if (!next) break;
      active.current += 1;
      const draft = draftFromFileName(next.file.name);
      void addTrack(next.file, draft, (stage) => {
        update(next.key, { state: stage });
      })
        .then((track) => {
          if (!track) {
            update(next.key, {
              state: "failed",
              detail: "the API did not return the track it created",
            });
            return;
          }
          update(next.key, {
            state: track.idempotent ? "already-in-library" : "added",
            title: track.title,
            artist: track.artist,
            trackId: track.id,
            detail: "",
          });
          added.current(track);
        })
        .catch((error: unknown) => {
          update(next.key, {
            state: "failed",
            detail: error instanceof Error ? error.message : String(error),
          });
        })
        .finally(() => {
          active.current -= 1;
          if (pending.current.length > 0 || active.current > 0) pump();
          else setRunning(false);
        });
    }
  }, [update]);

  const add = useCallback(
    (files: readonly File[]) => {
      if (files.length === 0) return;
      const queued = files.map((file) => {
        sequence.current += 1;
        const draft = draftFromFileName(file.name);
        return {
          key: `upload-${String(sequence.current)}`,
          file,
          entry: {
            key: `upload-${String(sequence.current)}`,
            fileName: file.name,
            title: draft.title,
            artist: draft.artist,
            byteSize: file.size,
            state: "waiting" as QueueState,
            detail: "",
            trackId: null,
          },
        };
      });
      setEntries((current) => [
        ...current,
        ...queued.map((item) => item.entry),
      ]);
      pending.current.push(
        ...queued.map((item) => ({ key: item.key, file: item.file })),
      );
      setRunning(true);
      pump();
    },
    [pump],
  );

  const clearSettled = useCallback(() => {
    setEntries((current) => current.filter((entry) => !isSettled(entry.state)));
  }, []);

  return { entries, running, add, clearSettled };
}
